import React from 'react';
import { Link } from 'react-router-dom';
import heroImage from '../assets/images/DAO Watch-A Text-Badge Mockup.jpg';
import ApiTest from '../components/ApiTest';

const HomePage = () => {
  const features = [
    {
      title: 'Intuitive Proposal Submission',
      description: 'Maintain an eligible DAO list and run a focused scan when preparing a new episode.',
      icon: (
        <svg className="w-6 h-6 text-primary-600" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2"></path>
        </svg>
      ),
    },
    {
      title: 'AI-Driven Processing',
      description: 'Automatically ranks proposal candidates and prepares structured review data.',
      icon: (
        <svg className="w-6 h-6 text-primary-600" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 10V3L4 14h7v7l9-11h-7z"></path>
        </svg>
      ),
    },
    {
      title: 'Notion Integration',
      description: 'Generate organized markdown notes grouped by episode.',
      icon: (
        <svg className="w-6 h-6 text-primary-600" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 7v8a2 2 0 002 2h6M8 7V5a2 2 0 012-2h4.586a1 1 0 01.707.293l4.414 4.414a1 1 0 01.293.707V15a2 2 0 01-2 2h-2M8 7H6a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2v-2"></path>
        </svg>
      ),
    },
    {
      title: 'Data Validation',
      description: 'Review 2 recent news stories per eligible DAO and 3 proposal candidates before publishing notes.',
      icon: (
        <svg className="w-6 h-6 text-primary-600" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"></path>
        </svg>
      ),
    },
  ];

  return (
    <div className="max-w-4xl mx-auto">
      <h1 className="text-4xl font-bold mb-6">DAO Watch Automation Tool</h1>
      
      <ApiTest />
      
      <div className="bg-white shadow-md rounded-lg p-6 mb-8">
        <h2 className="text-2xl font-semibold mb-4">Welcome to the DAO Watch Automation Tool</h2>
        <p className="mb-4">
          This tool helps streamline DAO Watch episode research by scanning your eligible DAO list for recent news and important governance proposals.
        </p>
        <p className="mb-4">
          Select an option below to get started:
        </p>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-6">
          <Link
            to="/submit"
            className="bg-primary-600 hover:bg-primary-700 text-white text-center font-semibold py-3 px-4 rounded-lg"
          >
            Start Research Scan
          </Link>
          
          <Link
            to="/history"
            className="bg-gray-600 hover:bg-gray-700 text-white text-center font-semibold py-3 px-4 rounded-lg"
          >
            View History
          </Link>
          
          <Link
            to="/markdown-viewer"
            className="bg-indigo-600 hover:bg-indigo-700 text-white text-center font-semibold py-3 px-4 rounded-lg"
          >
            Markdown Viewer
          </Link>
        </div>
      </div>
      
      <div className="bg-blue-50 border-l-4 border-blue-500 p-4 mb-8">
        <h3 className="text-lg font-semibold text-blue-700">Getting Started</h3>
        <ul className="list-disc ml-6 mt-2">
          <li className="mb-1">Add or remove DAOs from your eligible research list</li>
          <li className="mb-1">Auto-scan for 2 recent news stories per eligible DAO and 3 proposal candidates</li>
          <li className="mb-1">Generate markdown files for episode show notes</li>
          <li className="mb-1">View and edit markdown files in the Markdown Viewer</li>
        </ul>
      </div>

      {/* Hero Section */}
      <section className="bg-gradient-to-r from-primary-600 to-secondary-600 text-white rounded-lg overflow-hidden shadow-xl mb-12">
        <div className="container mx-auto px-4 py-12 md:py-20">
          <div className="flex flex-col md:flex-row items-center">
            <div className="md:w-1/2 mb-8 md:mb-0">
              <h1 className="text-4xl md:text-5xl font-bold mb-4">
                DAO Watch Automation Tool
              </h1>
              <p className="text-xl mb-6">
                Scan eligible DAOs for recent news and proposal candidates for your content series.
              </p>
              <div className="flex flex-col sm:flex-row space-y-4 sm:space-y-0 sm:space-x-4">
                <Link
                  to="/submit"
                  className="bg-white text-primary-600 hover:bg-gray-100 font-semibold py-3 px-6 rounded-lg shadow-md transition duration-300 text-center"
                >
                  Start Scan
                </Link>
                <a
                  href="#features"
                  className="bg-transparent hover:bg-white/10 border border-white text-white font-semibold py-3 px-6 rounded-lg transition duration-300 text-center"
                >
                  Learn More
                </a>
              </div>
            </div>
            <div className="md:w-1/2">
              <img
                src={heroImage}
                alt="DAO Watch Automation"
                className="rounded-lg shadow-lg max-w-full h-auto"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="py-12">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl font-bold text-center mb-12">Key Features</h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {features.map((feature, index) => (
              <div
                key={index}
                className="bg-white p-6 rounded-lg shadow-md hover:shadow-lg transition duration-300"
              >
                <div className="bg-primary-100 p-3 rounded-full w-12 h-12 flex items-center justify-center mb-4">
                  {feature.icon}
                </div>
                <h3 className="text-xl font-semibold mb-2">{feature.title}</h3>
                <p className="text-gray-600">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="bg-gray-100 rounded-lg p-8 mt-12">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl font-bold mb-4">Ready to Get Started?</h2>
          <p className="text-xl text-gray-600 mb-8 max-w-3xl mx-auto">
            Run one scan to collect recent DAO news, rank proposal candidates, and prepare the next episode notes.
          </p>
          <Link
            to="/submit"
            className="bg-primary-600 hover:bg-primary-700 text-white font-semibold py-3 px-8 rounded-lg shadow-md transition duration-300 inline-block"
          >
            Start Research Scan
          </Link>
        </div>
      </section>
    </div>
  );
};

export default HomePage; 
